import {
    getAllColumnsInAxisConfig,
    getAxisConfig,
    getChartType,
    getColumns,
    getSuggestedAxisConfig,
    parseQueryDefinitionFromVisualAxisConfig,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    configOptions,
    getDimensionHandlingMappingForTSHostedCharts,
    isCustomChart,
    isCustomOrTsHostedChart,
    isCustomOrTsHostedChartEnabled,
    isGeoChartType,
    isTsHostedChart,
    newExperienceChartTypes,
    validateAxisConfig,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { generateDefaultCustomChartConfigForTSHostedCharts } from '@thoughtspot/chart-lib/lib/core/custom-chart-config-utils';
import { containsMeasureNamesOrMeasureValuesColumns } from '@thoughtspot/chart-lib/lib/core/measure-names-and-values';
import { translate } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import {
    isCustomChartEnabled,
    isEnhancedCfEnabled,
    isTooltipCustomizationEnabled,
} from '@thoughtspot/session-service';
import {
    parseCustomChartConfigToCustomChartConfigInput,
    transformAxisConfigToCustomChartConfig,
    transformCustomChartConfigToAxisConfig,
} from '@thoughtspot/ts-chart-app';
import { getId } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import {
    AnswerColumn,
    AxisConfig,
    AxisConfigInput,
    ChartType,
    ChartViz,
    ChartVizProps,
    CustomChartConfigInput,
    DisplayMode,
} from '/@services/generated/graphql-types';
import { MetricsService } from '/@services/metrics-service/metrics-service';
import {
    reuseAxesIfPossible,
    unlinkAllInheritance,
} from '/@utils/viz/client-state.util';
import { toggleAllDataLabel } from '/@utils/viz/column-properties.util';

const logger = create('chart-service.util.ts');

export const isSystemColorDifferent = (
    oldChartType: ChartType,
    chartType: ChartType,
    chartViz: ChartViz,
): boolean => {
    const isCurrentChartGeo = isGeoChartType(oldChartType);
    const isNewChartGeo = isGeoChartType(chartType);
    const areChartCategoriesDifferent = isCurrentChartGeo !== isNewChartGeo;
    const isCurrentChartRainbow =
        !!oldChartType &&
        configOptions(oldChartType, chartViz).useRainbowColors;
    const isNewChartRainbow = configOptions(chartType, chartViz)
        .useRainbowColors;
    const isColorStrategyDifferent =
        isCurrentChartRainbow !== isNewChartRainbow;
    const isCurrentChartAlpha =
        !!oldChartType && configOptions(oldChartType, chartViz).useAlphaColors;
    const isNewChartAlpha = configOptions(chartType, chartViz).useAlphaColors;
    const isAlphaUseDifferent = isCurrentChartAlpha !== isNewChartAlpha;
    if (
        areChartCategoriesDifferent ||
        isColorStrategyDifferent ||
        isAlphaUseDifferent
    ) {
        return true;
    }
    return false;
};

export const resetTooltipConfig = (vizProps: ChartVizProps): ChartVizProps => {
    return {
        ...vizProps,
        tooltipConfig: null,
    };
};

function isSwitchingBetweenPivotTables(
    oldChartType: ChartType,
    newChartType: ChartType,
): boolean {
    const pivotTableTypes = new Set([
        ChartType.AdvancedPivotTable,
        ChartType.PivotTable,
    ]);
    return (
        pivotTableTypes.has(oldChartType) &&
        pivotTableTypes.has(newChartType) &&
        oldChartType !== newChartType
    );
}

const isSwitchingToAdvancedChart = (newChartType: ChartType): boolean => {
    return newExperienceChartTypes.includes(newChartType);
};

export const setChartType = (
    chartViz: ChartViz,
    chartType: ChartType,
    updateChartTypeMutationFunction: any,
    displayMode: DisplayMode,
    getAnswerMixpanelProperties: () => any,
    metricsService?: MetricsService,
    customChartGuid: string = null,
    updateCustomChartConfig?: (
        customChartConfigInput: CustomChartConfigInput[],
    ) => void,
): any => {
    const oldChartType = getChartType(chartViz);
    const oldCustomChartGuid = chartViz.config.customChartGuid;
    let modifiedClientState = _.cloneDeep(chartViz.vizProp);
    modifiedClientState = unlinkAllInheritance(modifiedClientState);
    const isSameCustomGuid =
        chartType === ChartType.CustomChart
            ? oldCustomChartGuid === customChartGuid
            : true; // would be null for non custom charts
    if (
        oldChartType === chartType &&
        displayMode === DisplayMode.ChartMode &&
        isSameCustomGuid
    ) {
        return false;
    }

    if (isCustomOrTsHostedChart(chartType)) {
        if (!isCustomChartEnabled() && isCustomChart(chartType)) {
            logger.error('Custom Chart is not enabled');
            return false;
        }
        
        if (
            !isCustomOrTsHostedChartEnabled(chartType) &&
            isTsHostedChart(chartType)
        ) {
            logger.error(`${chartType} is not enabled`);
            return false;
        }

        // If non ts hosted custom chart is selected,
        // and no guid is provided, then do not allow the chart to be set
        if (_.isEmpty(customChartGuid) && isCustomChart(chartType)) {
            logger.error(
                'Custom Chart Selected with no guid. Please report this issue',
            );
            return false;
        }

        if (isSwitchingBetweenPivotTables(oldChartType, chartType)) {
            const axisConfig = getAxisConfig(chartViz);
            const allColumns = getColumns(chartViz);
            const allVisualizedColumnIds = getAllColumnsInAxisConfig(
                axisConfig,
            ).map(col => col.id);
            const customConfigtoAxisConfigMap = getDimensionHandlingMappingForTSHostedCharts(
                chartType,
            );
            const customChartConfig = transformAxisConfigToCustomChartConfig(
                axisConfig,
                allColumns,
                allVisualizedColumnIds,
                customConfigtoAxisConfigMap,
            );
            const customChartConfigInput = parseCustomChartConfigToCustomChartConfigInput(
                customChartConfig,
            );

            return updateChartTypeMutationFunction(
                chartViz,
                modifiedClientState,
                [],
                [],
                chartType,
                customChartGuid,
            )
                .then(() => updateCustomChartConfig(customChartConfigInput))
                .catch((error: any) => {
                    logger.error(
                        'Failed to update custom chart config:',
                        error,
                    );
                });
        }

        if (isSwitchingToAdvancedChart(chartType)) {
            // The BE owns config generation for advanced charts via
            // handleChartTypeSwitch; send empty customChartConfig so the
            // BE result is used instead of an FE-transformed one.
            return updateChartTypeMutationFunction(
                chartViz,
                modifiedClientState,
                [],
                [],
                chartType,
                customChartGuid,
            ).catch((error: any) => {
                logger.error(
                    'Failed to update custom chart config:',
                    error,
                );
            });
        }

        const customChartConfig = isTsHostedChart(chartType)
            ? generateDefaultCustomChartConfigForTSHostedCharts(
                  chartViz,
                  chartType,
              )
            : null;

        return updateChartTypeMutationFunction(
            chartViz,
            modifiedClientState,
            [],
            customChartConfig,
            chartType,
            customChartGuid,
        );
    }

    if (metricsService) {
        const answerCommonProperties = getAnswerMixpanelProperties();
        metricsService.collectEvent(
            new UserAction(userActionTypeEnum.CHANGED_CHART_TYPE),
            {
                fromChartType: oldChartType,
                toChartType: chartType,
                ...answerCommonProperties,
            },
        );
    }
    if (
        isSystemColorDifferent(oldChartType, chartType, chartViz) &&
        chartViz.vizProp
    ) {
        modifiedClientState.systemSeriesColors = [];
        modifiedClientState.systemMultiColorSeriesColors = [];
    }
    let axisConfig = getAxisConfig(chartViz);

    // If current axis config containes measure names or measure values columns, then this axis config
    // can't be reused for other chart types as the measure name/value columns are not understood by those charts.
    let canReuseAxisConfig = !containsMeasureNamesOrMeasureValuesColumns(
        axisConfig,
    );

    if (isSwitchingBetweenPivotTables(oldChartType, chartType)) {
        const customChartConfig = chartViz.config.customChartConfig;
        const allColumns = getColumns(chartViz);
        const customConfigtoAxisConfigMap = getDimensionHandlingMappingForTSHostedCharts(
            oldChartType,
        );
        axisConfig = transformCustomChartConfigToAxisConfig(
            customChartConfig,
            allColumns,
            customConfigtoAxisConfigMap,
        );
        // Pivot Table and AdvancedPivotTable supports measure names and measure values columns
        canReuseAxisConfig = true;
    }

    const chartAxisConfigValidity = validateAxisConfig(
        chartType,
        axisConfig,
        chartViz,
        translate,
    );

    // TODO: add query defination check
    const isCurrentAxisConfigReusable =
        chartViz.config.isLocked &&
        chartAxisConfigValidity.isValid &&
        oldChartType !== ChartType.Kpi &&
        chartType !== ChartType.LineStackedColumn && // reset the axis config for line stacked column with BE suggested config.
        canReuseAxisConfig;

    if (
        chartViz.vizProp.chartProperties &&
        chartViz.vizProp.chartProperties.visibleSeriesNames &&
        chartViz.vizProp.chartProperties.visibleSeriesNames.length
    ) {
        modifiedClientState.chartProperties.visibleSeriesNames = [];
    }
    if (chartType === ChartType.Heatmap) {
        // change chartType from other to heatmap making all labels and datalabels checkbox
        modifiedClientState = toggleAllDataLabel('', chartViz, true);
    }
    if (isTooltipCustomizationEnabled()) {
        modifiedClientState = resetTooltipConfig(modifiedClientState);
    }
    // When isStackedAsPercent is enabled, Number Formatting is not available for pivot table
    if (chartType === ChartType.PivotTable) {
        modifiedClientState.chartProperties.chartSpecific.isStackedAsPercent = false;
    }
    if (
        !isCurrentAxisConfigReusable &&
        !!getSuggestedAxisConfig(chartViz, chartType)
    ) {
        const suggestedAxisconfig = getSuggestedAxisConfig(chartViz, chartType);
        const reuseAxisProperties = reuseAxesIfPossible(
            chartViz,
            suggestedAxisconfig,
            oldChartType,
            chartType,
        );
        if (reuseAxisProperties !== false) {
            modifiedClientState = {
                ...modifiedClientState,
                axisProperties: [...reuseAxisProperties],
            };
        }
        return updateChartTypeMutationFunction(
            chartViz,
            modifiedClientState,
            parseQueryDefinitionFromVisualAxisConfig(
                chartType,
                suggestedAxisconfig,
                chartViz,
            ),
            [],
            chartType,
        );
    }
    if (chartAxisConfigValidity.isValid) {
        const reuseAxisProperties = reuseAxesIfPossible(
            chartViz,
            axisConfig,
            oldChartType,
            chartType,
        );
        if (reuseAxisProperties !== false) {
            modifiedClientState = {
                ...modifiedClientState,
                axisProperties: [...reuseAxisProperties],
            };
        }
        return updateChartTypeMutationFunction(
            chartViz,
            modifiedClientState,
            parseQueryDefinitionFromVisualAxisConfig(
                chartType,
                axisConfig,
                chartViz,
            ),
            [],
            chartType,
        );
    }
    logger.error(
        'Invalid axis config for chart type',
        chartAxisConfigValidity.errors,
    );
    return false;
};
/**
 * getAxisConfigInputFromAxisConfig
 * @param axisConfig
 *
 * @return axisConfigInput: AxisConfigMutation
 *
 * Prism expects input type for axis config mutation.
 * This function generates AxisConfigInput from AxisConfig to be consumed
 * by prism mutation
 *
 */
const generateAxisConfigInput = (axisConfig: AxisConfig): AxisConfigInput => {
    return _.reduce(
        axisConfig,
        (acc: any, val: AnswerColumn, key) => {
            // TODO(chetan): the object that we get here is the chart query definition object
            // which also containers columnsInDataOrder. We want to skip adding that
            // to the query input. In the long run, this should be cleaner and we should
            // not need to do this.
            if (key === '__typename' || key === 'columnsInDataOrder')
                return acc;
            if (_.isArray(val)) {
                if (val.length !== 0) {
                    acc[key] = val.map((col: AnswerColumn) => col.id);
                }
                return acc;
            }
            if (val) {
                acc[key] = val.id;
            }
            return acc;
        },
        {},
    );
};
export const getAxisConfigInputFromAxisConfig = (
    axisConfig: AxisConfig[],
): AxisConfigInput[] => {
    const axisConfigInputFromAxisConfig: any = [];
    _.forEach(axisConfig, individualAxisConfig => {
        axisConfigInputFromAxisConfig.push(
            generateAxisConfigInput(individualAxisConfig),
        );
    });
    return axisConfigInputFromAxisConfig;
};

export const getAxisConfigKey = (columns: AnswerColumn[]) => {
    return _.reduce(
        columns,
        (key, column) => {
            // eslint-disable-next-line no-param-reassign
            key += `_${getId(column)}`;
            return key;
        },
        'key',
    );
};

/**
 *
 * @return shouldFetchExtraColumnsData: boolean
 *
 * In prism layer, this flag decides if data is to be fetched for some extra columns
 * which are not part of the axis config
 *
 */
export function getShouldFetchExtraColumnsData(): boolean {
    return !!isTooltipCustomizationEnabled() || !!isEnhancedCfEnabled();
}
